import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getLocationIntelligence, sendChatMessage, autoGenerateScenarios, getPlanningPolicy, getPlanningCilS106, getPlanningHousingNeeds, exportPlanningReport, getComparables, fmt, fmtPct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { MapPin, Send, Loader2, TrendingUp, Building2, Hammer, AlertTriangle, Sparkles, Wand2, GitCompare, ArrowRight, FileText, Shield, TreePine, Train, Landmark, Scale, CircleDot, Search, FileDown } from "lucide-react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix default marker icon for Leaflet in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

export default function AIPanel() {
  const [postcode, setPostcode] = useState("");
  const [activeTab, setActiveTab] = useState("analysis");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}`);
  const [autoLoading, setAutoLoading] = useState(false);
  const [generatedScenarios, setGeneratedScenarios] = useState(null);
  const [planningData, setPlanningData] = useState(null);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [cilData, setCilData] = useState(null);
  const [cilLoading, setCilLoading] = useState(false);
  const [housingData, setHousingData] = useState(null);
  const [housingLoading, setHousingLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [planningPostcode, setPlanningPostcode] = useState("");
  const [planningAddress, setPlanningAddress] = useState("");
  const [mapCenter, setMapCenter] = useState([51.5074, -0.1278]);
  const [mapMarker, setMapMarker] = useState(null);
  const [compsData, setCompsData] = useState(null);
  const [compsLoading, setCompsLoading] = useState(false);
  const [compsPostcode, setCompsPostcode] = useState("");
  const [mapLocationName, setMapLocationName] = useState("");
  const messagesEnd = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const runAnalysis = async () => {
    if (!postcode.trim()) return;
    setLoading(true);
    try {
      const res = await getLocationIntelligence(postcode.trim());
      if (res.success) {
        setAnalysis(res.data);
        toast.success("Location analysis complete");
      }
    } catch (e) {
      toast.error("Failed to get location data");
    } finally {
      setLoading(false);
    }
  };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatInput("");
    setChatMessages((p) => [...p, { role: "user", content: msg }]);
    setChatLoading(true);
    try {
      const res = await sendChatMessage(sessionId, msg);
      setChatMessages((p) => [...p, { role: "assistant", content: res.response }]);
    } catch (e) {
      toast.error("Chat error");
      setChatMessages((p) => [...p, { role: "assistant", content: "Sorry, I encountered an error. Please try again." }]);
    } finally {
      setChatLoading(false);
    }
  };

  const runAutoGenerate = async () => {
    if (!postcode.trim()) return;
    setAutoLoading(true);
    setGeneratedScenarios(null);
    try {
      const res = await autoGenerateScenarios(postcode.trim());
      if (res.success) {
        setGeneratedScenarios(res.scenarios);
        toast.success("3 scenarios generated successfully");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate scenarios");
    } finally {
      setAutoLoading(false);
    }
  };

  const compareGenerated = () => {
    if (!generatedScenarios || generatedScenarios.length < 2) return;
    const ids = generatedScenarios.map((s) => s.id).join(",");
    navigate(`/compare?ids=${ids}`);
  };

  const geocodePostcode = useCallback(async (pc) => {
    if (!pc.trim()) return;
    try {
      const resp = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(pc + ", UK")}&limit=1`);
      const data = await resp.json();
      if (data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        setMapCenter([lat, lng]);
        setMapMarker([lat, lng]);
      }
    } catch { /* silently fail geocoding */ }
  }, []);

  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const resp = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
      const data = await resp.json();
      const pc = data?.address?.postcode;
      const addr = data?.address;
      const name = [addr?.road, addr?.suburb || addr?.neighbourhood, addr?.city || addr?.town || addr?.village].filter(Boolean).join(", ");
      if (name) setMapLocationName(name);
      if (pc) {
        setPlanningPostcode(pc);
        return pc;
      }
    } catch { /* silently fail */ }
    return null;
  }, []);

  const handleMapClick = useCallback(async (lat, lng) => {
    setMapMarker([lat, lng]);
    setMapCenter([lat, lng]);
    const pc = await reverseGeocode(lat, lng);
    if (pc) {
      setPostcode(pc);
      setPlanningPostcode(pc);
      setCompsPostcode(pc);
      toast.success(`Postcode: ${pc}`);
      // Auto-trigger search on the active tab
      setTimeout(() => {
        if (activeTab === "analysis") runAnalysisForPostcode(pc);
        else if (activeTab === "comparables") runComparablesForPostcode(pc);
      }, 200);
    }
  }, [reverseGeocode, activeTab]);

  const runPlanningAnalysis = async (pc) => {
    const code = pc || planningPostcode;
    if (!code.trim() && !planningAddress.trim()) return;
    setPlanningLoading(true);
    setPlanningData(null);
    if (code.trim()) geocodePostcode(code);
    try {
      const res = await getPlanningPolicy(code.trim(), planningAddress.trim());
      if (res.success) {
        setPlanningData(res.data);
        toast.success("Planning policy analysis complete");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Service temporarily unavailable";
      toast.error(`Planning analysis failed: ${msg.substring(0, 80)}. Click Analyze to retry.`);
    } finally {
      setPlanningLoading(false);
    }
  };

  const runCilAnalysis = async () => {
    const code = planningPostcode.trim();
    const addr = planningAddress.trim();
    if (!code && !addr) return;
    setCilLoading(true); setCilData(null);
    try {
      const res = await getPlanningCilS106(code, addr);
      if (res.success) { setCilData(res.data); toast.success("CIL & S106 analysis complete"); }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Service temporarily unavailable";
      toast.error(`CIL/S106 analysis failed: ${msg.substring(0, 80)}. Click Analyze to retry.`);
    }
    finally { setCilLoading(false); }
  };

  const runHousingAnalysis = async () => {
    const code = planningPostcode.trim();
    const addr = planningAddress.trim();
    if (!code && !addr) return;
    setHousingLoading(true); setHousingData(null);
    try {
      const res = await getPlanningHousingNeeds(code, addr);
      if (res.success) { setHousingData(res.data); toast.success("Housing needs analysis complete"); }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Service temporarily unavailable";
      toast.error(`Housing needs analysis failed: ${msg.substring(0, 80)}. Click Analyze to retry.`);
    }
    finally { setHousingLoading(false); }
  };

  const handleExportReport = async (format) => {
    setExportLoading(true);
    try {
      const blob = await exportPlanningReport(format, planningData || {}, cilData || {}, housingData || {});
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Planning_Report.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch { toast.error("Failed to export report"); }
    finally { setExportLoading(false); }
  };

  const runComparables = async () => {
    if (!compsPostcode.trim()) return;
    setCompsLoading(true);
    setCompsData(null);
    try {
      const res = await getComparables(compsPostcode.trim());
      if (res.success) {
        setCompsData(res.data);
        toast.success("Comparable evidence loaded");
      }
    } catch (e) {
      toast.error("Failed to get comparables");
    } finally {
      setCompsLoading(false);
    }
  };

  // Variants that accept a postcode directly (called from map click)
  const runAnalysisForPostcode = async (pc) => {
    if (!pc) return;
    setLoading(true);
    try {
      const res = await getLocationIntelligence(pc);
      if (res.success) { setAnalysis(res.data); toast.success("Location analysis complete"); }
    } catch { toast.error("Failed to get location data"); }
    finally { setLoading(false); }
  };

  const runComparablesForPostcode = async (pc) => {
    if (!pc) return;
    setCompsLoading(true);
    setCompsData(null);
    try {
      const res = await getComparables(pc);
      if (res.success) { setCompsData(res.data); toast.success("Comparable evidence loaded"); }
    } catch { toast.error("Failed to get comparables"); }
    finally { setCompsLoading(false); }
  };

  const d = analysis;
  const rentals = d?.rental_estimates;

  const LocationMapBlock = ({ height = 280 }) => (
    <Card style={{ overflow: "hidden", marginBottom: 20 }}>
      <div style={{ height }} data-testid="location-map">
        <MapContainer center={mapCenter} zoom={14} style={{ width: "100%", height: "100%" }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onClick={handleMapClick} />
          <MapRecenter center={mapCenter} />
          {mapMarker && <Marker position={mapMarker} />}
        </MapContainer>
      </div>
      <CardContent style={{ padding: "8px 16px" }}>
        <p style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>
          <MapPin size={12} style={{ display: "inline", marginRight: 4 }} />
          Click on the map to select a location, or type a postcode above.
        </p>
      </CardContent>
    </Card>
  );

  return (
    <div className="page-enter" style={{ padding: "32px 40px" }}>
      <h1 style={{ fontFamily: "Manrope", fontWeight: 800, fontSize: 28, marginBottom: 8 }} data-testid="ai-title">
        Location Intelligence
      </h1>
      <p style={{ color: "hsl(var(--muted-foreground))", fontSize: 14, marginBottom: 24 }}>
        AI-powered location analysis and market chat
      </p>

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="ai-tabs">
        <TabsList>
          <TabsTrigger value="analysis" data-testid="tab-analysis">
            <MapPin size={14} style={{ marginRight: 6 }} />Location Analysis
          </TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat">
            <Sparkles size={14} style={{ marginRight: 6 }} />Market Chat
          </TabsTrigger>
          <TabsTrigger value="planning" data-testid="tab-planning">
            <FileText size={14} style={{ marginRight: 6 }} />Planning Policy
          </TabsTrigger>
          <TabsTrigger value="comparables" data-testid="tab-comparables">
            <Search size={14} style={{ marginRight: 6 }} />Comparables
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analysis" style={{ marginTop: 20 }}>
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <Input
              placeholder="Enter UK postcode (e.g. E14 9SH, SW1A 1AA)"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runAnalysis()}
              style={{ maxWidth: 360, fontFamily: "JetBrains Mono" }}
              data-testid="postcode-input"
            />
            <Button
              onClick={() => { geocodePostcode(postcode); runAnalysis(); }}
              disabled={loading || !postcode.trim()}
              style={{ background: "hsl(var(--foreground))", color: "white" }}
              data-testid="analyze-btn"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />}
              <span style={{ marginLeft: 8 }}>{loading ? "Analyzing..." : "Analyze"}</span>
            </Button>
          </div>

          <LocationMapBlock />

          {d && !d.raw_analysis && (
            <div className="animate-in" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Card>
                <CardHeader style={{ paddingBottom: 12 }}>
                  <CardTitle style={{ fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                    <MapPin size={16} style={{ color: "hsl(var(--accent))" }} /> {d.area_name || postcode}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p style={{ fontSize: 14, color: "hsl(var(--muted-foreground))", lineHeight: 1.6 }}>{d.summary}</p>
                  {d.market_outlook && (
                    <p style={{ fontSize: 14, color: "hsl(var(--muted-foreground))", lineHeight: 1.6, marginTop: 12, fontStyle: "italic" }}>
                      {d.market_outlook}
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader style={{ paddingBottom: 12 }}>
                  <CardTitle style={{ fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                    <Building2 size={16} style={{ color: "hsl(var(--accent))" }} /> Rental Estimates (PCM)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {rentals && (
                    <div style={{ display: "grid", gap: 8 }}>
                      {[
                        { label: "Studio", data: rentals.studio },
                        { label: "1 Bed", data: rentals.one_bed },
                        { label: "2 Bed", data: rentals.two_bed },
                        { label: "3 Bed", data: rentals.three_bed },
                      ].map((r) => (
                        <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid hsl(var(--border))" }}>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{r.label}</span>
                          <span className="data-cell" style={{ fontSize: 13 }}>
                            {r.data ? `\u00A3${r.data.low} - \u00A3${r.data.high}` : "-"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader style={{ paddingBottom: 12 }}>
                  <CardTitle style={{ fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                    <TrendingUp size={16} style={{ color: "hsl(var(--accent))" }} /> Investment Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ display: "grid", gap: 8 }}>
                    {[
                      { label: "Yield (NIY)", val: d.yield_estimate ? `${d.yield_estimate.low}% - ${d.yield_estimate.high}%` : "-" },
                      { label: "Construction Cost /sqft", val: d.construction_cost_psf ? `\u00A3${d.construction_cost_psf.low} - \u00A3${d.construction_cost_psf.high}` : "-" },
                      { label: "Land Value /unit", val: d.land_value_per_unit ? `\u00A3${d.land_value_per_unit.low?.toLocaleString()} - \u00A3${d.land_value_per_unit.high?.toLocaleString()}` : "-" },
                    ].map((m) => (
                      <div key={m.label} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid hsl(var(--border))" }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{m.label}</span>
                        <span className="data-cell" style={{ fontSize: 13 }}>{m.val}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader style={{ paddingBottom: 12 }}>
                  <CardTitle style={{ fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                    <AlertTriangle size={16} style={{ color: "hsl(var(--warning))" }} /> Drivers & Risks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {d.demand_drivers && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--success))", marginBottom: 6 }}>
                        Demand Drivers
                      </div>
                      {d.demand_drivers.map((x, i) => (
                        <div key={i} style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", padding: "2px 0" }}>
                          + {x}
                        </div>
                      ))}
                    </div>
                  )}
                  {d.risks && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--destructive))", marginBottom: 6 }}>
                        Key Risks
                      </div>
                      {d.risks.map((x, i) => (
                        <div key={i} style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", padding: "2px 0" }}>
                          - {x}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {d?.raw_analysis && (
            <Card>
              <CardContent style={{ padding: 24, whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.6 }}>
                {d.raw_analysis}
              </CardContent>
            </Card>
          )}

          {d && (
            <div style={{ marginTop: 24 }}>
              <Card style={{ border: "1px dashed hsl(var(--border))", background: "hsl(var(--muted) / 0.3)" }}>
                <CardContent style={{ padding: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                    <div>
                      <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "Manrope", display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <Wand2 size={18} /> Auto-Generate Scenarios
                      </h3>
                      <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", maxWidth: 420 }}>
                        Generate Downside, Base Case, and Upside scenarios from this location data with one click.
                      </p>
                    </div>
                    <Button
                      onClick={runAutoGenerate}
                      disabled={autoLoading}
                      data-testid="auto-generate-btn"
                      style={{ background: "hsl(var(--foreground))", color: "white", minWidth: 180 }}
                    >
                      {autoLoading ? (
                        <><Loader2 size={16} className="animate-spin" /><span style={{ marginLeft: 8 }}>Generating...</span></>
                      ) : (
                        <><Wand2 size={16} /><span style={{ marginLeft: 8 }}>Generate 3 Scenarios</span></>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {autoLoading && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "hsl(var(--muted-foreground))" }}>
                  <Loader2 size={28} className="animate-spin" style={{ margin: "0 auto 12px" }} />
                  <p style={{ fontSize: 14 }}>AI is generating 3 scenario variants...</p>
                  <p style={{ fontSize: 12, marginTop: 4 }}>This may take 15-30 seconds</p>
                </div>
              )}

              {generatedScenarios && (
                <div className="animate-in" style={{ marginTop: 16 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                    {generatedScenarios.map((s) => {
                      const k = s.results?.kpis || {};
                      const variant = s.variant || "";
                      const variantColor = variant.includes("downside") ? "hsl(var(--destructive))"
                        : variant.includes("upside") ? "hsl(var(--success))"
                        : "hsl(var(--accent))";
                      return (
                        <Card key={s.id} data-testid={`generated-scenario-${s.variant}`} style={{ borderTop: `3px solid ${variantColor}` }}>
                          <CardHeader style={{ paddingBottom: 8 }}>
                            <CardTitle style={{ fontSize: 14, fontWeight: 700 }}>
                              {s.project_name}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div style={{ display: "grid", gap: 6 }}>
                              {[
                                { label: "GDV", val: fmt(k.total_gdv) },
                                { label: "Total Cost", val: fmt(k.total_costs) },
                                { label: "Profit", val: fmt(k.dev_profit) },
                                { label: "Profit on Cost", val: fmtPct(k.dev_profit_on_cost) },
                                { label: "Ungeared IRR", val: fmtPct(k.ungeared_irr) },
                              ].map((m) => (
                                <div key={m.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: "1px solid hsl(var(--border))" }}>
                                  <span style={{ color: "hsl(var(--muted-foreground))" }}>{m.label}</span>
                                  <span style={{ fontFamily: "JetBrains Mono", fontWeight: 600 }}>{m.val}</span>
                                </div>
                              ))}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              style={{ width: "100%", marginTop: 12, fontSize: 12 }}
                              onClick={() => navigate(`/appraisal/${s.id}`)}
                              data-testid={`view-scenario-${s.variant}`}
                            >
                              View Full Appraisal <ArrowRight size={12} style={{ marginLeft: 6 }} />
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                  <div style={{ textAlign: "center", marginTop: 20 }}>
                    <Button
                      onClick={compareGenerated}
                      data-testid="compare-generated-btn"
                      style={{ background: "hsl(var(--foreground))", color: "white", minWidth: 220 }}
                    >
                      <GitCompare size={16} />
                      <span style={{ marginLeft: 8 }}>Compare All 3 Scenarios</span>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="chat" style={{ marginTop: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div>
              <LocationMapBlock height={420} />
              {/* Pre-defined quick queries */}
              <Card style={{ marginTop: -8 }}>
                <CardContent style={{ padding: "14px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "hsl(var(--muted-foreground))", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Quick Market Queries {mapLocationName && <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— {mapLocationName}</span>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {[
                      { label: "Average house prices & rental values", query: `What are the average house prices and rental values in ${mapLocationName || postcode || "this area"}? Break down by property type (1-bed flat, 2-bed flat, 3-bed house, etc).` },
                      { label: "Typical land values (all uses)", query: `What are typical land values in ${mapLocationName || postcode || "this area"} for all land uses including residential, commercial, industrial, and agricultural? Provide per acre/hectare estimates.` },
                      { label: "Key developers (10+ dwelling schemes)", query: `Who are the key developers active within a 25-mile radius of ${mapLocationName || postcode || "this area"} who have submitted planning applications for development of 10 or more dwellings in the last 3 years? List their schemes, unit counts, and current status.` },
                      { label: "Average build costs", query: `What are the average build costs per square foot in ${mapLocationName || postcode || "this area"} for residential development? Break down by type: apartments (low/mid/high-rise), houses, student accommodation, and commercial.` },
                    ].map((q, i) => (
                      <button
                        key={i}
                        onClick={() => { setChatInput(q.query); }}
                        data-testid={`quick-query-${i}`}
                        style={{
                          textAlign: "left", padding: "8px 12px", borderRadius: 6, fontSize: 13,
                          border: "1px solid hsl(var(--border))", background: "hsl(var(--background))",
                          cursor: "pointer", color: "hsl(var(--foreground))", lineHeight: 1.3,
                          transition: "background 0.15s"
                        }}
                        onMouseEnter={(e) => e.target.style.background = "hsl(var(--muted) / 0.5)"}
                        onMouseLeave={(e) => e.target.style.background = "hsl(var(--background))"}
                      >
                        {q.label}
                      </button>
                    ))}
                    {/* Market demand selector */}
                    <div style={{ fontSize: 12, fontWeight: 600, color: "hsl(var(--muted-foreground))", marginTop: 6, marginBottom: 2 }}>Market demand by land use:</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {["Residential", "Commercial", "Light Industrial", "PBSA", "Hotel"].map((use) => (
                        <button
                          key={use}
                          onClick={() => setChatInput(`What is the current market demand for ${use.toLowerCase()} development in ${mapLocationName || postcode || "this area"}? Include supply pipeline, vacancy rates, absorption rates, and development activity.`)}
                          data-testid={`demand-${use.toLowerCase().replace(/\s/g, "-")}`}
                          style={{
                            padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                            border: "1px solid hsl(var(--border))", background: "hsl(var(--background))",
                            cursor: "pointer", color: "hsl(var(--foreground))", transition: "all 0.15s"
                          }}
                          onMouseEnter={(e) => { e.target.style.background = "hsl(var(--accent))"; e.target.style.color = "white"; e.target.style.borderColor = "hsl(var(--accent))"; }}
                          onMouseLeave={(e) => { e.target.style.background = "hsl(var(--background))"; e.target.style.color = "hsl(var(--foreground))"; e.target.style.borderColor = "hsl(var(--border))"; }}
                        >
                          {use}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            <Card style={{ height: 640, display: "flex", flexDirection: "column" }}>
              <div className="chat-messages" style={{ flex: 1, overflowY: "auto" }}>
                {chatMessages.length === 0 && (
                  <div style={{ textAlign: "center", padding: "60px 20px", color: "hsl(var(--muted-foreground))" }}>
                    <Sparkles size={32} style={{ margin: "0 auto 16px", opacity: 0.4 }} />
                    <p style={{ fontSize: 14 }}>Drop a pin on the map and select a query, or ask anything about UK property markets.</p>
                    {mapLocationName && <p style={{ fontSize: 13, marginTop: 8, fontWeight: 500, color: "hsl(var(--foreground))" }}>Current location: {mapLocationName}</p>}
                  </div>
                )}
                {chatMessages.map((m, i) => (
                  <div key={i} className={`chat-msg ${m.role}`} data-testid={`chat-msg-${i}`}>
                    {m.content}
                  </div>
                ))}
                {chatLoading && (
                  <div className="chat-msg assistant" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Loader2 size={14} className="animate-spin" /> Thinking...
                  </div>
                )}
                <div ref={messagesEnd} />
              </div>
              <div className="chat-input-bar">
                <Input
                  placeholder={mapLocationName ? `Ask about ${mapLocationName}...` : "Ask about UK property markets..."}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !chatLoading && sendChat()}
                  disabled={chatLoading}
                  style={{ flex: 1 }}
                  data-testid="chat-input"
                />
                <Button
                  onClick={sendChat}
                  disabled={chatLoading || !chatInput.trim()}
                  style={{ background: "hsl(var(--foreground))", color: "white" }}
                  data-testid="send-chat-btn"
                >
                  <Send size={16} />
                </Button>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="planning" style={{ marginTop: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {/* Left: Map + Input */}
            <div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                <Input
                  placeholder="Site address (e.g. 123 Harbour Road, Bristol)"
                  value={planningAddress}
                  onChange={(e) => setPlanningAddress(e.target.value)}
                  style={{ fontFamily: "inherit" }}
                  data-testid="planning-address-input"
                />
                <div style={{ display: "flex", gap: 12 }}>
                  <Input
                    placeholder="Postcode (e.g. E14 9SH)"
                    value={planningPostcode}
                    onChange={(e) => setPlanningPostcode(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && runPlanningAnalysis()}
                    style={{ flex: 1, fontFamily: "JetBrains Mono" }}
                    data-testid="planning-postcode-input"
                  />
                  <Button
                    onClick={() => runPlanningAnalysis()}
                    disabled={planningLoading || (!planningPostcode.trim() && !planningAddress.trim())}
                    style={{ background: "hsl(var(--foreground))", color: "white" }}
                    data-testid="planning-analyze-btn"
                  >
                    {planningLoading ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                    <span style={{ marginLeft: 8 }}>{planningLoading ? "Analyzing..." : "Analyze"}</span>
                  </Button>
                </div>
              </div>
              <LocationMapBlock height={380} />
            </div>

            {/* Right: Results - 3 sections */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: "calc(100vh - 200px)", overflowY: "auto", paddingRight: 4 }}>
              {/* Export buttons */}
              {(planningData || cilData || housingData) && (
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <Button variant="outline" size="sm" disabled={exportLoading} onClick={() => handleExportReport("pdf")} data-testid="export-planning-pdf" style={{ gap: 4 }}>
                    <FileDown size={13} /> PDF
                  </Button>
                  <Button variant="outline" size="sm" disabled={exportLoading} onClick={() => handleExportReport("docx")} data-testid="export-planning-docx" style={{ gap: 4 }}>
                    <FileDown size={13} /> Word
                  </Button>
                </div>
              )}

              {/* Section 1: Planning Policy */}
              <Card style={{ borderLeft: "3px solid hsl(var(--accent))" }}>
                <CardHeader style={{ paddingBottom: 8, display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                  <CardTitle style={{ fontSize: 14 }}>Planning Policy (NPPF, Local Plan, SPDs)</CardTitle>
                  <Button size="sm" onClick={() => runPlanningAnalysis()} disabled={planningLoading || (!planningPostcode.trim() && !planningAddress.trim())} data-testid="run-policy-btn" style={{ height: 28, fontSize: 12, gap: 4 }}>
                    {planningLoading ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} {planningData ? "Re-analyze" : "Analyze"}
                    </Button>
                </CardHeader>
                <CardContent>
                  {planningLoading && <div style={{ textAlign: "center", padding: "20px", color: "hsl(var(--muted-foreground))" }}><Loader2 size={20} className="animate-spin" style={{ margin: "0 auto 8px" }} /><p style={{ fontSize: 12 }}>Analyzing NPPF, Local Plan &amp; SPDs...</p></div>}
                  {!planningData && !planningLoading && <p style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>Click Analyze to review NPPF, NPPG, extant &amp; emerging Local Plans, and SPDs</p>}
                  {planningData && !planningData.raw_analysis && (
                    <div className="animate-in" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }} data-testid="planning-authority">{planningData.local_authority}</div>
                      {planningData.nppf && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--accent))", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>NPPF</div>
                          <PolicyRow label="Version" value={planningData.nppf.version} />
                          <PolicyRow label="Presumption" value={planningData.nppf.presumption_in_favour} />
                          <PolicyRow label="HDT" value={planningData.nppf.housing_delivery_test} />
                          {planningData.nppf.key_policies?.map((p, i) => <div key={i} style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", padding: "2px 0", lineHeight: 1.4 }}>{p}</div>)}
                        </div>
                      )}
                      {planningData.national_planning_practice_guidance?.relevant_guidance?.length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--accent))", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>NPPG</div>
                          {planningData.national_planning_practice_guidance.relevant_guidance.map((g, i) => <div key={i} style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", padding: "2px 0", lineHeight: 1.4 }}>{g}</div>)}
                        </div>
                      )}
                      {planningData.extant_local_plan && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--accent))", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>Extant Local Plan</div>
                          <PolicyRow label="Document" value={planningData.extant_local_plan.document_name} />
                          <PolicyRow label="Adopted" value={planningData.extant_local_plan.adoption_date} />
                          <PolicyRow label="Allocation" value={planningData.extant_local_plan.site_allocation} />
                          {planningData.extant_local_plan.key_policies?.map((p, i) => (
                            <div key={i} style={{ fontSize: 12, background: "hsl(var(--muted) / 0.3)", padding: "6px 10px", borderRadius: 4, marginTop: 4, lineHeight: 1.4 }}>
                              <span style={{ fontWeight: 600 }}>{p.policy_ref} — {p.policy_name}</span>
                              <div style={{ color: "hsl(var(--muted-foreground))" }}>{p.summary}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {planningData.emerging_local_plan && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(38 92% 50%)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>Emerging Local Plan</div>
                          <PolicyRow label="Document" value={planningData.emerging_local_plan.document_name} />
                          <PolicyRow label="Stage" value={planningData.emerging_local_plan.current_stage} />
                          <PolicyRow label="Weight" value={planningData.emerging_local_plan.weight_in_decision_making} />
                        </div>
                      )}
                      {planningData.supplementary_planning_documents?.length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--accent))", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>SPDs</div>
                          {planningData.supplementary_planning_documents.map((spd, i) => (
                            <div key={i} style={{ fontSize: 12, padding: "3px 0", lineHeight: 1.4 }}>
                              <span style={{ fontWeight: 600 }}>{spd.document_name}</span>
                              {spd.adoption_date && <span style={{ color: "hsl(var(--muted-foreground))", fontSize: 11 }}> ({spd.adoption_date})</span>}
                              <div style={{ color: "hsl(var(--muted-foreground))" }}>{spd.relevance}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {planningData.key_risks?.length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--destructive))", textTransform: "uppercase", marginBottom: 4 }}>Risks</div>
                          {planningData.key_risks.map((r, i) => <div key={i} style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", lineHeight: 1.4 }}>- {r}</div>)}
                        </div>
                      )}
                    </div>
                  )}
                  {planningData?.raw_analysis && <p style={{ fontSize: 12, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{planningData.raw_analysis}</p>}
                </CardContent>
              </Card>

              {/* Section 2: CIL & S106 */}
              <Card style={{ borderLeft: "3px solid hsl(142 60% 40%)" }}>
                <CardHeader style={{ paddingBottom: 8, display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                  <CardTitle style={{ fontSize: 14 }}>CIL & S106 Guidance</CardTitle>
                  <Button size="sm" onClick={runCilAnalysis} disabled={cilLoading || (!planningPostcode.trim() && !planningAddress.trim())} data-testid="run-cil-btn" style={{ height: 28, fontSize: 12, gap: 4 }}>
                    {cilLoading ? <Loader2 size={12} className="animate-spin" /> : <Landmark size={12} />} {cilData ? "Re-analyze" : "Analyze"}
                  </Button>
                </CardHeader>
                <CardContent>
                  {cilLoading && <div style={{ textAlign: "center", padding: "20px", color: "hsl(var(--muted-foreground))" }}><Loader2 size={20} className="animate-spin" style={{ margin: "0 auto 8px" }} /><p style={{ fontSize: 12 }}>Analyzing CIL &amp; S106...</p></div>}
                  {!cilData && !cilLoading && <p style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>Click Analyze to review CIL charging schedule and S106 guidance</p>}
                  {cilData && !cilData.raw_analysis && (
                    <div className="animate-in" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {cilData.cil_charging_schedule && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(142 60% 40%)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>CIL Charging Schedule</div>
                          <PolicyRow label="Adopted" value={cilData.cil_charging_schedule.adopted ? "Yes" : "No"} />
                          <PolicyRow label="Residential Rate" value={cilData.cil_charging_schedule.residential_rate} />
                          <PolicyRow label="Mayoral CIL" value={cilData.cil_charging_schedule.mayoral_cil} />
                          <PolicyRow label="Reliefs" value={cilData.cil_charging_schedule.relief_exemptions} />
                        </div>
                      )}
                      {cilData.s106_guidance && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(142 60% 40%)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4, marginTop: 4 }}>S106 Obligations</div>
                          <PolicyRow label="Affordable Housing" value={cilData.s106_guidance.affordable_housing_requirement} />
                          <PolicyRow label="Threshold" value={cilData.s106_guidance.affordable_housing_threshold} />
                          <PolicyRow label="BTR Route" value={cilData.s106_guidance.btr_route} />
                          <PolicyRow label="Viability Review" value={cilData.s106_guidance.viability_review_mechanism} />
                          {cilData.s106_guidance.other_typical_obligations?.map((o, i) => <div key={i} style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>- {o}</div>)}
                        </div>
                      )}
                      {cilData.estimated_costs && (
                        <div style={{ marginTop: 4 }}>
                          <PolicyRow label="CIL Estimate" value={cilData.estimated_costs.cil_estimate_note} />
                          <PolicyRow label="S106 Range" value={cilData.estimated_costs.s106_typical_range} />
                        </div>
                      )}
                    </div>
                  )}
                  {cilData?.raw_analysis && <p style={{ fontSize: 12, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{cilData.raw_analysis}</p>}
                </CardContent>
              </Card>

              {/* Section 3: Housing Needs */}
              <Card style={{ borderLeft: "3px solid hsl(38 92% 50%)" }}>
                <CardHeader style={{ paddingBottom: 8, display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                  <CardTitle style={{ fontSize: 14 }}>SHLAA, Housing Needs & Demand</CardTitle>
                  <Button size="sm" onClick={runHousingAnalysis} disabled={housingLoading || (!planningPostcode.trim() && !planningAddress.trim())} data-testid="run-housing-btn" style={{ height: 28, fontSize: 12, gap: 4 }}>
                    {housingLoading ? <Loader2 size={12} className="animate-spin" /> : <Building2 size={12} />} {housingData ? "Re-analyze" : "Analyze"}
                  </Button>
                </CardHeader>
                <CardContent>
                  {housingLoading && <div style={{ textAlign: "center", padding: "20px", color: "hsl(var(--muted-foreground))" }}><Loader2 size={20} className="animate-spin" style={{ margin: "0 auto 8px" }} /><p style={{ fontSize: 12 }}>Analyzing SHLAA, housing needs &amp; demand...</p></div>}
                  {!housingData && !housingLoading && <p style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>Click Analyze to review SHLAA, housing needs assessment and demand study</p>}
                  {housingData && !housingData.raw_analysis && (
                    <div className="animate-in" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {housingData.shlaa && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(38 92% 50%)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>SHLAA / SHELAA</div>
                          <PolicyRow label="Document" value={housingData.shlaa.document_name} />
                          <PolicyRow label="Site Included" value={housingData.shlaa.site_included} />
                          <PolicyRow label="Capacity" value={housingData.shlaa.identified_capacity} />
                          <PolicyRow label="Deliverability" value={housingData.shlaa.deliverability_assessment} />
                        </div>
                      )}
                      {housingData.housing_needs_assessment && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(38 92% 50%)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4, marginTop: 4 }}>Housing Needs Assessment</div>
                          <PolicyRow label="Document" value={housingData.housing_needs_assessment.document_name} />
                          <PolicyRow label="Annual Need" value={housingData.housing_needs_assessment.annual_housing_need} />
                          <PolicyRow label="Affordable Need" value={housingData.housing_needs_assessment.affordable_need} />
                          <PolicyRow label="Tenure" value={housingData.housing_needs_assessment.tenure_breakdown} />
                          <PolicyRow label="Unit Mix" value={housingData.housing_needs_assessment.unit_size_mix} />
                          <PolicyRow label="BTR/PRS" value={housingData.housing_needs_assessment.btr_prs_need} />
                        </div>
                      )}
                      {housingData.local_demand_study && (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(38 92% 50%)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4, marginTop: 4 }}>Local Demand Study</div>
                          <PolicyRow label="Document" value={housingData.local_demand_study.document_name} />
                          <PolicyRow label="Pop Growth" value={housingData.local_demand_study.population_growth} />
                          <PolicyRow label="Demographics" value={housingData.local_demand_study.target_demographics} />
                          {housingData.local_demand_study.key_findings?.map((f, i) => <div key={i} style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", lineHeight: 1.4 }}>{f}</div>)}
                        </div>
                      )}
                      {housingData.five_year_supply && (
                        <div style={{ marginTop: 4 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--destructive))", textTransform: "uppercase", marginBottom: 4 }}>5-Year Supply</div>
                          <PolicyRow label="Position" value={housingData.five_year_supply.position} />
                          <PolicyRow label="Shortfall" value={housingData.five_year_supply.shortfall} />
                          <PolicyRow label="Implications" value={housingData.five_year_supply.implications} />
                        </div>
                      )}
                    </div>
                  )}
                  {housingData?.raw_analysis && <p style={{ fontSize: 12, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{housingData.raw_analysis}</p>}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="comparables" style={{ marginTop: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {/* Left: Map + Input */}
            <div>
              <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                <Input
                  placeholder="Enter UK postcode (e.g. E14 9SH)"
                  value={compsPostcode}
                  onChange={(e) => setCompsPostcode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runComparables()}
                  style={{ flex: 1, fontFamily: "JetBrains Mono" }}
                  data-testid="comps-postcode-input"
                />
                <Button
                  onClick={() => { geocodePostcode(compsPostcode); runComparables(); }}
                  disabled={compsLoading || !compsPostcode.trim()}
                  style={{ background: "hsl(var(--foreground))", color: "white" }}
                  data-testid="comps-analyze-btn"
                >
                  {compsLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                  <span style={{ marginLeft: 8 }}>{compsLoading ? "Searching..." : "Find Comparables"}</span>
                </Button>
              </div>
              <LocationMapBlock height={420} />
            </div>

            {/* Right: Results */}
            <div>
              {compsLoading && (
                <div style={{ textAlign: "center", padding: "80px 20px", color: "hsl(var(--muted-foreground))" }}>
                  <Loader2 size={28} className="animate-spin" style={{ margin: "0 auto 12px" }} />
                  <p style={{ fontSize: 14 }}>Researching comparable evidence...</p>
                  <p style={{ fontSize: 12, marginTop: 4 }}>This may take 15-30 seconds</p>
                </div>
              )}

              {!compsData && !compsLoading && (
                <div style={{ textAlign: "center", padding: "80px 20px", color: "hsl(var(--muted-foreground))" }}>
                  <Search size={40} style={{ margin: "0 auto 16px", opacity: 0.3 }} />
                  <p style={{ fontSize: 14, fontWeight: 500 }}>Enter a postcode to find comparable evidence</p>
                  <p style={{ fontSize: 13, marginTop: 4 }}>AI-sourced land transactions, rental comparables, and investment sales</p>
                </div>
              )}

              {compsData && !compsData.raw_analysis && (
                <div className="animate-in" style={{ display: "flex", flexDirection: "column", gap: 16, maxHeight: "calc(100vh - 300px)", overflowY: "auto", paddingRight: 4 }}>
              {/* Market Summary */}
              {compsData.market_summary && (
                <Card style={{ borderLeft: "4px solid hsl(var(--accent))" }}>
                  <CardContent style={{ padding: "16px 20px" }}>
                    <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "Manrope" }} data-testid="comps-area-name">
                      {compsData.area_name} — Comparable Evidence
                    </div>
                    <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>Search radius: {compsData.search_radius}</div>
                    <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", lineHeight: 1.6, marginTop: 8 }}>{compsData.market_summary}</p>
                  </CardContent>
                </Card>
              )}

              {/* Land Transactions */}
              {compsData.land_transactions?.length > 0 && (
                <Card>
                  <CardHeader style={{ paddingBottom: 8 }}>
                    <CardTitle style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                      <Landmark size={15} style={{ color: "hsl(var(--accent))" }} /> Land Transactions ({compsData.land_transactions.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent style={{ padding: "0 20px 16px" }}>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="land-transactions-table">
                        <thead>
                          <tr style={{ borderBottom: "2px solid hsl(var(--border))" }}>
                            {["Address", "Date", "Price", "Acres", "Units", "Per Unit", "Buyer", "Status"].map(h => (
                              <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.03em", color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {compsData.land_transactions.map((t, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                              <td style={{ padding: "8px 10px", maxWidth: 180 }}>{t.address}</td>
                              <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{t.date}</td>
                              <td style={{ padding: "8px 10px", fontFamily: "JetBrains Mono", whiteSpace: "nowrap" }}>{fmt(t.price)}</td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>{t.site_area_acres}</td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>{t.units_planned}</td>
                              <td style={{ padding: "8px 10px", fontFamily: "JetBrains Mono", whiteSpace: "nowrap" }}>{fmt(t.price_per_unit)}</td>
                              <td style={{ padding: "8px 10px" }}>{t.buyer}</td>
                              <td style={{ padding: "8px 10px", fontSize: 12 }}>{t.planning_status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Rental Comparables */}
              {compsData.rental_comparables?.length > 0 && (
                <Card>
                  <CardHeader style={{ paddingBottom: 8 }}>
                    <CardTitle style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                      <Building2 size={15} style={{ color: "hsl(var(--accent))" }} /> Rental Comparables ({compsData.rental_comparables.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent style={{ padding: "0 20px 16px" }}>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="rental-comparables-table">
                        <thead>
                          <tr style={{ borderBottom: "2px solid hsl(var(--border))" }}>
                            {["Scheme", "Operator", "Year", "Units", "Occ %", "Studio", "1 Bed", "2 Bed", "3 Bed", "S/C pcm"].map(h => (
                              <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.03em", color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {compsData.rental_comparables.map((r, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                              <td style={{ padding: "8px 10px", maxWidth: 160 }}>
                                <div style={{ fontWeight: 600 }}>{r.scheme_name}</div>
                                <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{r.address}</div>
                              </td>
                              <td style={{ padding: "8px 10px" }}>{r.operator}</td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>{r.completion_year}</td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>{r.total_units}</td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>{r.occupancy_pct}%</td>
                              <td style={{ padding: "8px 10px", fontFamily: "JetBrains Mono" }}>{fmt(r.rents?.studio)}</td>
                              <td style={{ padding: "8px 10px", fontFamily: "JetBrains Mono" }}>{fmt(r.rents?.one_bed)}</td>
                              <td style={{ padding: "8px 10px", fontFamily: "JetBrains Mono" }}>{fmt(r.rents?.two_bed)}</td>
                              <td style={{ padding: "8px 10px", fontFamily: "JetBrains Mono" }}>{fmt(r.rents?.three_bed)}</td>
                              <td style={{ padding: "8px 10px", fontFamily: "JetBrains Mono" }}>{fmt(r.service_charge_pcm)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Investment Comparables */}
              {compsData.investment_comparables?.length > 0 && (
                <Card>
                  <CardHeader style={{ paddingBottom: 8 }}>
                    <CardTitle style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                      <TrendingUp size={15} style={{ color: "hsl(var(--accent))" }} /> Investment Sales ({compsData.investment_comparables.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent style={{ padding: "0 20px 16px" }}>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="investment-comparables-table">
                        <thead>
                          <tr style={{ borderBottom: "2px solid hsl(var(--border))" }}>
                            {["Scheme", "Date", "Price", "Units", "Per Unit", "NIY", "Buyer", "Seller"].map(h => (
                              <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.03em", color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {compsData.investment_comparables.map((inv, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                              <td style={{ padding: "8px 10px", maxWidth: 160 }}>
                                <div style={{ fontWeight: 600 }}>{inv.scheme_name}</div>
                                <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{inv.address}</div>
                              </td>
                              <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{inv.date}</td>
                              <td style={{ padding: "8px 10px", fontFamily: "JetBrains Mono", whiteSpace: "nowrap" }}>{fmt(inv.price)}</td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>{inv.units}</td>
                              <td style={{ padding: "8px 10px", fontFamily: "JetBrains Mono", whiteSpace: "nowrap" }}>{fmt(inv.price_per_unit)}</td>
                              <td style={{ padding: "8px 10px", fontFamily: "JetBrains Mono" }}>{inv.net_initial_yield}%</td>
                              <td style={{ padding: "8px 10px" }}>{inv.buyer}</td>
                              <td style={{ padding: "8px 10px" }}>{inv.seller}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {compsData?.raw_analysis && (
            <Card>
              <CardContent style={{ padding: 24, whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.6 }}>
                {compsData.raw_analysis}
              </CardContent>
            </Card>
          )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PolicyRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid hsl(var(--border))", gap: 12 }}>
      <span style={{ fontSize: 13, fontWeight: 500, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", textAlign: "right" }}>{String(value)}</span>
    </div>
  );
}

function MapClickHandler({ onClick }) {
  useMapEvents({
    click(e) {
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}
